import { useCallback, useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";

import { wpxTableMessages } from "../wpxTable.intl";

import { APP_NAME } from "@/app/constants/constants";
import { ACTION_ID, SELECTOR_ID } from "@/app/constants/global-constants";
import { customToast } from "@/components/alerts/CustomToast";
import { typedLocalStorage } from "@/utils/storage/localStorage";

interface UseColumnOrderProps {
  tableId: string;
  defaultColumns: string[];
  isColumnDraggable: boolean;
  priorityColumns?: string[];
}

interface UseColumnOrderReturn {
  columnOrder: string[];
  setColumnOrder: (
    order: string[] | ((previousColumnOrder: string[]) => string[]),
  ) => void;
  resetColumnOrder: () => void;
}

const PINNED_COLUMN_IDS = new Set([SELECTOR_ID, ACTION_ID]);

const withoutPinnedColumns = (columnIds: string[]): string[] => {
  return columnIds.filter((columnId) => !PINNED_COLUMN_IDS.has(columnId));
};

export const prioritizeColumnIds = (
  columnIds: string[],
  priorityColumns: string[],
): string[] => {
  if (!priorityColumns.length) {
    return columnIds;
  }

  const columnIdsSet = new Set(columnIds);
  const priorityColumnIds = priorityColumns.filter((columnId) =>
    columnIdsSet.has(columnId),
  );

  const prioritySet = new Set(priorityColumnIds);
  const remaining = columnIds.filter((columnId) => !prioritySet.has(columnId));

  return [...priorityColumnIds, ...remaining];
};

const resolveDefaultColumnOrder = (
  defaultColumns: string[],
  priorityColumns: string[],
): string[] => {
  const unpinnedColumns = withoutPinnedColumns(defaultColumns);
  const prioritizedColumns = prioritizeColumnIds(
    unpinnedColumns,
    priorityColumns,
  );

  return injectPinnedColumns(prioritizedColumns, defaultColumns);
};

const injectPinnedColumns = (
  orderedColumnIds: string[],
  defaultColumnIds: string[],
): string[] => {
  const hasSelectorColumn = defaultColumnIds.includes(SELECTOR_ID);
  const hasActionColumn = defaultColumnIds.includes(ACTION_ID);
  const defaultOrderedColumnIds = withoutPinnedColumns(defaultColumnIds);
  const defaultOrderedColumnSet = new Set(defaultOrderedColumnIds);
  const validOrderedColumnIds = orderedColumnIds.filter((columnId) => {
    return defaultOrderedColumnSet.has(columnId);
  });
  const validOrderedColumnSet = new Set(validOrderedColumnIds);
  const missingColumnIds = defaultOrderedColumnIds.filter((columnId) => {
    return !validOrderedColumnSet.has(columnId);
  });
  const mergedOrderedColumnIds = [...validOrderedColumnIds];

  for (const missingColumnId of missingColumnIds) {
    const defaultColumnIndex = defaultOrderedColumnIds.indexOf(missingColumnId);
    let insertionIndex = -1;

    for (
      let columnIndex = defaultColumnIndex - 1;
      columnIndex >= 0;
      columnIndex--
    ) {
      const previousColumnId = defaultOrderedColumnIds[columnIndex];
      const previousColumnIndex =
        mergedOrderedColumnIds.indexOf(previousColumnId);

      if (previousColumnIndex !== -1) {
        insertionIndex = previousColumnIndex + 1;
        break;
      }
    }

    if (insertionIndex === -1) {
      for (
        let columnIndex = defaultColumnIndex + 1;
        columnIndex < defaultOrderedColumnIds.length;
        columnIndex++
      ) {
        const nextColumnId = defaultOrderedColumnIds[columnIndex];
        const nextColumnIndex = mergedOrderedColumnIds.indexOf(nextColumnId);

        if (nextColumnIndex !== -1) {
          insertionIndex = nextColumnIndex;
          break;
        }
      }
    }

    if (insertionIndex === -1) {
      insertionIndex = mergedOrderedColumnIds.length;
    }

    mergedOrderedColumnIds.splice(insertionIndex, 0, missingColumnId);
  }

  return [
    ...(hasSelectorColumn ? [SELECTOR_ID] : []),
    ...mergedOrderedColumnIds,
    ...(hasActionColumn ? [ACTION_ID] : []),
  ];
};

const loadColumnOrder = (storageKey: string): string[] | null => {
  const parsed = typedLocalStorage.getItem(
    storageKey as `${typeof APP_NAME}_table_column_order_${string}`,
  );

  return Array.isArray(parsed) && parsed.length > 0 ? parsed : null;
};

const resolveInitialColumnOrder = (
  storageKey: string,
  defaultColumns: string[],
  isColumnDraggable: boolean,
  priorityColumns: string[] = [],
): string[] => {
  const resolvedDefaultColumns = resolveDefaultColumnOrder(
    defaultColumns,
    priorityColumns,
  );

  if (!isColumnDraggable) {
    return resolvedDefaultColumns;
  }

  const savedOrder = loadColumnOrder(storageKey);
  if (!savedOrder) {
    return resolvedDefaultColumns;
  }

  if (!defaultColumns.length) {
    return savedOrder;
  }

  const savedOrderableColumns = withoutPinnedColumns(savedOrder);

  const prioritizedColumns = prioritizeColumnIds(
    savedOrderableColumns,
    priorityColumns,
  );

  return injectPinnedColumns(prioritizedColumns, resolvedDefaultColumns);
};

/**
 * Maintains persisted user column order while keeping selector and action columns fixed.
 *
 * @param {UseColumnOrderProps} props - Table identity, default columns, and drag capability.
 * @returns {UseColumnOrderReturn} Current order plus mutation and reset handlers.
 */
export const useColumnOrder = ({
  tableId,
  defaultColumns,
  isColumnDraggable,
  priorityColumns = [],
}: UseColumnOrderProps): UseColumnOrderReturn => {
  const intl = useIntl();
  const storageKey = useMemo(
    () => `${APP_NAME}_table_column_order_${tableId}`,
    [tableId],
  );
  const resolvedDefaultColumns = useMemo(() => {
    return resolveDefaultColumnOrder(defaultColumns, priorityColumns);
  }, [defaultColumns, priorityColumns]);

  const [columnOrder, setColumnOrderState] = useState<string[]>(() => {
    if (!isColumnDraggable) {
      try {
        typedLocalStorage.removeItem(
          storageKey as `${typeof APP_NAME}_table_column_order_${string}`,
        );
      } catch {
        customToast.error(
          intl.formatMessage(wpxTableMessages.columnOrderCleanupFailed),
        );
      }

      return resolvedDefaultColumns;
    }

    return resolveInitialColumnOrder(
      storageKey,
      resolvedDefaultColumns,
      isColumnDraggable,
      priorityColumns,
    );
  });

  const [trackedStorageKey, setTrackedStorageKey] = useState(storageKey);

  if (trackedStorageKey !== storageKey) {
    setTrackedStorageKey(storageKey);
    setColumnOrderState(
      resolveInitialColumnOrder(
        storageKey,
        resolvedDefaultColumns,
        isColumnDraggable,
        priorityColumns,
      ),
    );
  }

  // Preserve user order while allowing newly available columns to appear.
  useEffect(() => {
    if (!isColumnDraggable) {
      return;
    }

    if (!defaultColumns.length) {
      return;
    }

    setColumnOrderState((previousColumnOrder) => {
      const prioritizedColumnIds = prioritizeColumnIds(
        withoutPinnedColumns(previousColumnOrder),
        priorityColumns,
      );

      const mergedColumnOrder = injectPinnedColumns(
        prioritizedColumnIds,
        resolvedDefaultColumns,
      );

      return mergedColumnOrder.join(",") === previousColumnOrder.join(",")
        ? previousColumnOrder
        : mergedColumnOrder;
    });
  }, [isColumnDraggable, priorityColumns, resolvedDefaultColumns]);

  useEffect(() => {
    if (!isColumnDraggable || !defaultColumns.length) {
      return;
    }

    try {
      const orderedColumnIds = withoutPinnedColumns(columnOrder);
      typedLocalStorage.setItem(
        storageKey as `${typeof APP_NAME}_table_column_order_${string}`,
        orderedColumnIds,
      );
    } catch {
      // Column order should not block table rendering when storage is unavailable.
    }
  }, [columnOrder, defaultColumns.length, storageKey, isColumnDraggable]);

  const setColumnOrder = useCallback(
    (order: string[] | ((previousColumnOrder: string[]) => string[])): void => {
      if (!isColumnDraggable) {
        return;
      }

      setColumnOrderState((previousColumnOrder) => {
        const nextColumnOrder =
          typeof order === "function" ? order(previousColumnOrder) : order;

        return injectPinnedColumns(nextColumnOrder, resolvedDefaultColumns);
      });
    },
    [isColumnDraggable, resolvedDefaultColumns],
  );

  const resetColumnOrder = useCallback((): void => {
    setColumnOrderState(resolvedDefaultColumns);
    try {
      typedLocalStorage.removeItem(
        storageKey as `${typeof APP_NAME}_table_column_order_${string}`,
      );
    } catch {
      customToast.error(
        intl.formatMessage(wpxTableMessages.columnOrderResetFailed),
      );
    }
  }, [intl, resolvedDefaultColumns, storageKey]);

  return {
    columnOrder,
    setColumnOrder,
    resetColumnOrder,
  };
};
